import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';

function fmt(val) {
  if (val == null || val === '' || isNaN(val)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val));
}

function fmtDiff(val) {
  if (val == null || isNaN(val)) return '$0.00';
  const n = Number(val);
  const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(n));
  if (n < 0) return `-${formatted}`;
  return formatted;
}

export const ProjectSummaryTable = ({ rooms, onRoomClick }) => {
  const summary = useMemo(() => {
    let totalActual = 0, totalBudget = 0, totalDesigner = 0, totalEssentials = 0;

    const roomSummaries = rooms.map(room => {
      let roomActual = 0, roomBudget = 0, roomDesigner = 0, roomEssentials = 0;

      (room.items || []).forEach(item => {
        const ordered = Number(item.ordered_price) || 0;
        const budget = Number(item.budget) || 0;
        const listed = Number(item.listed_price) || 0;

        roomActual += ordered;
        roomBudget += budget;
        roomDesigner += listed;

        if (item.is_essential) {
          roomEssentials += ordered;
        }
      });

      totalActual += roomActual;
      totalBudget += roomBudget;
      totalDesigner += roomDesigner;
      totalEssentials += roomEssentials;

      return {
        id: room.id,
        name: room.name,
        actual: roomActual,
        budget: roomBudget,
        compared: roomBudget - roomActual,
        designer: roomDesigner,
        essentials: roomEssentials,
      };
    });

    return {
      rooms: roomSummaries,
      totalActual,
      totalBudget,
      totalCompared: totalBudget - totalActual,
      totalDesigner,
      totalEssentials,
    };
  }, [rooms]);

  return (
    <Card className="shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_30px_rgba(16,24,40,0.06)]" data-testid="project-summary-table">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg">Project Summary</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#2d948c]/10">
                <TableHead className="font-heading font-semibold text-[#1f2933] min-w-[160px]">Room</TableHead>
                <TableHead className="font-heading font-semibold text-[#1f2933] text-right tabular-nums">Actual Totals</TableHead>
                <TableHead className="font-heading font-semibold text-[#1f2933] text-right tabular-nums">Budget</TableHead>
                <TableHead className="font-heading font-semibold text-[#1f2933] text-right tabular-nums">Compared to Budget</TableHead>
                <TableHead className="font-heading font-semibold text-[#1f2933] text-right tabular-nums">Designer Totals</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.rooms.map(r => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-[#2d948c]/5 transition-colors"
                  onClick={() => onRoomClick(r.id)}
                  data-testid={`summary-row-${r.id}`}
                >
                  <TableCell className="font-semibold text-[#1f2933]">{r.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.actual)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.budget)}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${r.compared < 0 ? 'text-destructive' : 'text-[#2f7d5a]'}`}>
                    {fmtDiff(r.compared)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.designer)}</TableCell>
                </TableRow>
              ))}

              {summary.rooms.length > 0 && (
                <>
                  <TableRow className="border-t-2 border-[#1f2933]/20">
                    <TableCell className="font-heading font-bold text-[#1f2933] text-base">Total</TableCell>
                    <TableCell className="text-right tabular-nums font-heading font-bold text-base">{fmt(summary.totalActual)}</TableCell>
                    <TableCell className="text-right tabular-nums font-heading font-bold text-base">{fmt(summary.totalBudget)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-heading font-bold text-base ${summary.totalCompared < 0 ? 'text-destructive' : 'text-[#2f7d5a]'}`}>
                      {fmtDiff(summary.totalCompared)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-heading font-bold text-base">{fmt(summary.totalDesigner)}</TableCell>
                  </TableRow>
                  {summary.totalEssentials > 0 && (
                    <TableRow>
                      <TableCell className="font-medium text-[#5b6773]">Essentials Total</TableCell>
                      <TableCell className="text-right tabular-nums text-[#5b6773]">{fmt(summary.totalEssentials)}</TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </>
              )}

              {summary.rooms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#5b6773]">
                    No rooms added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
